import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Line, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import {
  fetchSuperAdminStats,
  fetchSubscriptionDistribution,
  fetchOrgGrowth,
  fetchSystemHealth,
  fetchRecentActions
} from '../../../actioncreators/actioncreators';
import './SuperAdmin.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Compact SVG Icons
const DashboardIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/>
  </svg>
);

const GlobeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
  </svg>
);

const BuildingIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z"/>
  </svg>
);

const CheckCircleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
  </svg>
);

const TimerIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M15 1H9v2h6V1zm-4 13h2V8h-2v6zm8.03-6.61l1.42-1.42c-.43-.51-.9-.99-1.41-1.41l-1.42 1.42C16.07 4.74 14.12 4 12 4c-4.97 0-9 4.03-9 9s4.03 9 9 9 9-4.03 9-9c0-2.12-.74-4.07-1.97-5.61zM12 20c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z"/>
  </svg>
);

const UsersIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
  </svg>
);

const PieChartIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M11 2v20c-5.07-.5-9-4.79-9-10s3.93-9.5 9-10zm2.03 0v8.99H22c-.47-4.74-4.24-8.52-8.97-8.99zm0 11.01V22c4.74-.47 8.5-4.25 8.97-8.99h-8.97z"/>
  </svg>
);

const TrendingUpIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z"/>
  </svg>
);

const HealthIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.5 9.5c-1.03 0-1.9.62-2.29 1.5h-2.92c-.39-.88-1.26-1.5-2.29-1.5s-1.9.62-2.29 1.5H6.79c-.39-.88-1.26-1.5-2.29-1.5C3.12 9.5 2 10.62 2 12s1.12 2.5 2.5 2.5c1.03 0 1.9-.62 2.29-1.5h2.92c.39.88 1.26 1.5 2.29 1.5s1.9-.62 2.29-1.5h2.92c.39.88 1.26 1.5 2.29 1.5 1.38 0 2.5-1.12 2.5-2.5s-1.12-2.5-2.5-2.5z"/>
  </svg>
);

const ActivityIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
  </svg>
);

const ServerIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M2 20h20v-4H2v4zm2-3h2v2H4v-2zM2 4v4h20V4H2zm4 3H4V5h2v2zm-4 7h20v-4H2v4zm2-3h2v2H4v-2z"/>
  </svg>
);

const AddIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
  </svg>
);

const EditIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
  </svg>
);

const DeleteIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
  </svg>
);

const NoDataIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zM7 10h2v7H7zm4-3h2v10h-2zm4 6h2v4h-2z"/>
  </svg>
);

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [subDistribution, setSubDistribution] = useState([]);
  const [orgGrowth, setOrgGrowth] = useState([]);
  const [systemHealth, setSystemHealth] = useState([]);
  const [recentActions, setRecentActions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Navigation handlers for KPI cards
  const handleCardClick = (filterType, filterValue) => {
    navigate('/home/super-admin/organizations', {
      state: { filterType, filterValue }
    });
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, subRes, growthRes, healthRes, actionsRes] = await Promise.allSettled([
        fetchSuperAdminStats(),
        fetchSubscriptionDistribution(),
        fetchOrgGrowth(),
        fetchSystemHealth(),
        fetchRecentActions()
      ]);

      if (statsRes.status === 'fulfilled') {
        setStats(statsRes.value?.data?.data || {});
      }
      if (subRes.status === 'fulfilled') {
        const subData = subRes.value?.data?.data;
        setSubDistribution(Array.isArray(subData) ? subData : []);
      }
      if (growthRes.status === 'fulfilled') {
        const growthData = growthRes.value?.data?.data;
        setOrgGrowth(Array.isArray(growthData) ? growthData : []);
      }
      if (healthRes.status === 'fulfilled') {
        const healthData = healthRes.value?.data?.data;
        setSystemHealth(Array.isArray(healthData) ? healthData : []);
      }
      if (actionsRes.status === 'fulfilled') {
        const actionsData = actionsRes.value?.data?.data;
        setRecentActions(Array.isArray(actionsData) ? actionsData : []);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    const n = Number(num);
    if (isNaN(n)) return '0';
    return n.toLocaleString();
  };

  const planLabelMap = { Trial: 'Trial', Basic: 'Basic', Advance: 'Advanced' };
  const getPlanLabel = (name) => planLabelMap[name] || name;

  const pieData = {
    labels: subDistribution.map(item => getPlanLabel(item.package_name)),
    datasets: [{
      data: subDistribution.map(item => Number(item.org_count)),
      backgroundColor: [
        'rgba(99, 102, 241, 0.85)',
        'rgba(16, 185, 129, 0.85)',
        'rgba(249, 115, 22, 0.85)',
        'rgba(14, 165, 233, 0.85)',
        'rgba(236, 72, 153, 0.85)',
      ],
      borderColor: 'white',
      borderWidth: 2,
      hoverOffset: 4,
    }]
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          padding: 12,
          usePointStyle: true,
          pointStyle: 'circle',
          font: { size: 11 },
          color: '#64748b',
        },
        onClick: (event, legendItem, legend) => {
          const planType = subDistribution[legendItem.index]?.package_type;
          if (planType) handleCardClick('plan', planType);
        },
      },
      tooltip: {
        backgroundColor: '#1e293b',
        titleFont: { size: 11, weight: '1000' },
        bodyFont: { size: 11 },
        padding: 8,
        cornerRadius: 6,
      },
    },
    onClick: (event, elements) => {
      if (elements.length > 0) {
        const index = elements[0].index;
        const planType = subDistribution[index]?.package_type;
        if (planType) handleCardClick('plan', planType);
      }
    },
  };

  const lineData = {
    labels: orgGrowth.map(item => {
      const date = new Date(item.date);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }),
    datasets: [{
      label: 'New Organizations',
      data: orgGrowth.map(item => item.new_orgs),
      borderColor: '#10b981',
      backgroundColor: 'rgba(16, 185, 129, 0.08)',
      borderWidth: 2,
      fill: true,
      tension: 0.4,
      pointBackgroundColor: '#10b981',
      pointBorderColor: '#fff',
      pointBorderWidth: 1.5,
      pointRadius: 3,
      pointHoverRadius: 5,
    }]
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#1e293b',
        titleFont: { size: 11, weight: '1000' },
        bodyFont: { size: 11 },
        padding: 8,
        cornerRadius: 6,
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 10 }, color: '#94a3b8', maxRotation: 0 },
      },
      y: {
        grid: { color: 'rgba(0, 0, 0, 0.04)' },
        ticks: { font: { size: 10 }, color: '#94a3b8' },
        beginAtZero: true,
      },
    },
  };

  const getActionClass = (actionType) => {
    if (actionType?.includes('CREATE') || actionType?.includes('ADD')) return 'action-create';
    if (actionType?.includes('UPDATE') || actionType?.includes('CHANGE') || actionType?.includes('TOGGLE')) return 'action-update';
    if (actionType?.includes('DELETE') || actionType?.includes('DISABLE') || actionType?.includes('SUSPEND')) return 'action-delete';
    return 'action-default';
  };

  const getActionIcon = (actionType) => {
    if (actionType?.includes('CREATE') || actionType?.includes('ADD')) return <AddIcon />;
    if (actionType?.includes('UPDATE') || actionType?.includes('CHANGE') || actionType?.includes('TOGGLE')) return <EditIcon />;
    if (actionType?.includes('DELETE') || actionType?.includes('DISABLE') || actionType?.includes('SUSPEND')) return <DeleteIcon />;
    return <ActivityIcon />;
  };

  const getStatusClass = (status) => {
    if (status === 'OK') return 'status-ok';
    if (status === 'DEGRADED') return 'status-degraded';
    return 'status-error';
  };

  const formatActionType = (actionType) => {
    return actionType?.replace(/_/g, ' ') || 'Unknown Action';
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="super-admin-dashboard">
        <div className="loading-container">
          <Spinner animation="border" />
          <span className="loading-text">Loading dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="super-admin-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <h2>
          <span className="header-icon"><DashboardIcon /></span>
          SUPER ORG_ADMIN <span style={{ color: '#6366f1', margin: '0 4px' }}>â€º</span> DASHBOARD
        </h2>
        <div className="platform-badge">
          <GlobeIcon />
          PLATFORM LEVEL
        </div>
      </div>

      {/* KPI Cards */}
      <Row className="g-3 mb-3">
        <Col xl={3} lg={6} md={6} sm={6}>
          <Card
            className="stat-card total-orgs clickable-card"
            onClick={() => handleCardClick('all', '')}
            style={{ cursor: 'pointer' }}
          >
            <Card.Body>
              <div className="stat-icon"><BuildingIcon /></div>
              <div className="stat-content">
                <div className="stat-label">Total Orgs</div>
                <h3 className="stat-value">{formatNumber(stats.total_orgs)}</h3>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xl={3} lg={6} md={6} sm={6}>
          <Card
            className="stat-card active-orgs clickable-card"
            onClick={() => handleCardClick('status', 'ACTIVE')}
            style={{ cursor: 'pointer' }}
          >
            <Card.Body>
              <div className="stat-icon"><CheckCircleIcon /></div>
              <div className="stat-content">
                <div className="stat-label">Active Orgs</div>
                <h3 className="stat-value">{formatNumber(stats.total_active_orgs)}</h3>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xl={3} lg={6} md={6} sm={6}>
          <Card
            className="stat-card trial-orgs clickable-card"
            onClick={() => handleCardClick('plan', 'Trial')}
            style={{ cursor: 'pointer' }}
          >
            <Card.Body>
              <div className="stat-icon"><TimerIcon /></div>
              <div className="stat-content">
                <div className="stat-label">Trial Orgs</div>
                <h3 className="stat-value">{formatNumber(stats.total_trial_orgs)}</h3>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xl={3} lg={6} md={6} sm={6}>
          <Card
            className="stat-card total-users clickable-card"
            onClick={() => navigate('/home/super-admin/users')}
            style={{ cursor: 'pointer' }}
          >
            <Card.Body>
              <div className="stat-icon"><UsersIcon /></div>
              <div className="stat-content">
                <div className="stat-label">Total Users</div>
                <h3 className="stat-value">{formatNumber(stats.total_users)}</h3>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Charts Row */}
      <Row className="g-3 mb-3">
        <Col lg={6}>
          <Card className="chart-card">
            <Card.Header>
              <div className="chart-icon pie-icon"><PieChartIcon /></div>
              Subscription Distribution
            </Card.Header>
            <Card.Body>
              <div className="chart-container">
                {subDistribution.length > 0 ? (
                  <Pie data={pieData} options={pieOptions} />
                ) : (
                  <div className="no-data-message">
                    <NoDataIcon />
                    <p>No subscription data</p>
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={6}>
          <Card className="chart-card">
            <Card.Header>
              <div className="chart-icon line-icon"><TrendingUpIcon /></div>
              Org Growth Trend (30 Days)
            </Card.Header>
            <Card.Body>
              <div className="chart-container">
                {orgGrowth.length > 0 ? (
                  <Line data={lineData} options={lineOptions} />
                ) : (
                  <div className="no-data-message">
                    <NoDataIcon />
                    <p>No growth data</p>
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Bottom Row - System Health and Recent Actions */}
      <Row className="g-3">
        {systemHealth.length > 0 && (
          <Col lg={4}>
            <Card className="health-card">
              <Card.Header>
                <div className="health-icon"><HealthIcon /></div>
                System Health
              </Card.Header>
              <Card.Body>
                {systemHealth.map((service, idx) => (
                  <div key={idx} className="health-item">
                    <span className="service-name">
                      <ServerIcon />
                      {service.service_name}
                    </span>
                    <span className={`status-badge ${getStatusClass(service.status)}`}>
                      {service.status}
                    </span>
                  </div>
                ))}
              </Card.Body>
            </Card>
          </Col>
        )}
        <Col lg={systemHealth.length > 0 ? 8 : 12}>
          <Card className="actions-card">
            <Card.Header>
              <div className="actions-icon"><ActivityIcon /></div>
              Recent Critical Actions
            </Card.Header>
            <Card.Body>
              {recentActions.length > 0 ? (
                recentActions.map((action, idx) => (
                  <div key={idx} className={`action-item ${getActionClass(action.action_type)}`}>
                    <div className="action-icon-wrapper">
                      {getActionIcon(action.action_type)}
                    </div>
                    <div className="action-content">
                      <div className="action-type">{formatActionType(action.action_type)}</div>
                      <div className="action-target">{action.target_org_name || 'N/A'}</div>
                    </div>
                    <div className="action-time">{formatTime(action.timestamp)}</div>
                  </div>
                ))
              ) : (
                <div className="no-data-message">
                  <NoDataIcon />
                  <p>No recent actions</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default SuperAdminDashboard;
